import { DateField } from "@/features/FilterTable/DateField";
import {
  dateSchema,
  formatDateValueToISOString,
  parseToDateValue,
} from "@/features/FormCard/utils";
import { postClientAction } from "@/lib/fetch";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Clients } from "../../../lib/clients";
import { useCurrentPageFromUrl } from "@/hooks/useClientActions";
import { TriggerMode } from "../types/trigger_mode";
import { ClientActionPostPayload } from "../types/clientActionPostPayload";
import { ExportType } from "../types/export_type";

const GenerateWithoutLookThroughSchema = z.object({
  reportingDate: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .describe("date"),
  portfolio: z.string(),
});

const generateWithoutLookThroughFields =
  GenerateWithoutLookThroughSchema.keyof().enum;

export type GenerateReportWithoutLookThroughSchemaType = z.infer<
  typeof GenerateWithoutLookThroughSchema
>;

interface G4GenerateWithoutLookThroughPayload extends ClientActionPostPayload {
  mode: TriggerMode;
  export_type: ExportType;
  [generateWithoutLookThroughFields.portfolio]: string;
}

export default function G4GenerateReportoutLookThrough() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPortflio, setSelectedPortfolio] = useState<string>("");
  const [selectedPortflios, setSelectedPortfolios] = useState<string[]>([]);

  const { handleSubmit, setValue, watch, register, reset } =
    useForm<GenerateReportWithoutLookThroughSchemaType>({
      mode: "onChange",
      defaultValues: {
        [generateWithoutLookThroughFields.reportingDate]: undefined,
        [generateWithoutLookThroughFields.portfolio]: "",
      },
      resolver: zodResolver(
        GenerateWithoutLookThroughSchema,
      ) as Resolver<GenerateReportWithoutLookThroughSchemaType>,
    });

  useEffect(() => {
    setValue(generateWithoutLookThroughFields.portfolio, selectedPortflio, {
      shouldValidate: true,
    });
  }, [selectedPortflios, setValue]);

  const selectedDate = watch(generateWithoutLookThroughFields.reportingDate);
  const selectedPortfolio: string =
    watch(generateWithoutLookThroughFields.portfolio) ?? "";
  const canSubmit = selectedPortfolio != "" && selectedDate != null;

  const currentPage = useCurrentPageFromUrl();

  const generatePdfMutation = useMutation({
    mutationFn: async (data: GenerateReportWithoutLookThroughSchemaType) => {
      const formattedDate = formatDateValueToISOString(data.reportingDate);

      const payload: G4GenerateWithoutLookThroughPayload = {
        [generateWithoutLookThroughFields.reportingDate]: formattedDate ?? "",
        [generateWithoutLookThroughFields.portfolio]: data.portfolio,
        action_button_name: t("reports.generate_without_lookthrough_action"),
        action_execution_page: currentPage ?? "",
        mode: "calculate_and_export",
        prefix: Clients.G4,
        client: Clients.G4.toUpperCase(),
        export_type: "g4_non_transpa",
      };

      return await postClientAction(
        `${import.meta.env.VITE_BACKEND_URL}/${Clients.G4}/g4_download_center/run-lookthrough-export`,
        payload,
      );
    },
    onSuccess: () => {
      toast.success(t("reports.generate_started"));
      setIsOpen(false);
      reset();
      setSelectedPortfolios([]);
    },
    onError: () => {
      toast.error(t("reports.generate_error"));
    },
  });

  const handlePortfolioSelectionChange = (pages: string[]) => {
    setSelectedPortfolios(pages);
    if (pages.length == 1) {
      setSelectedPortfolio(pages[0]);
    } else {
      setSelectedPortfolio("");
    }
  };

  const handleFormSubmit = (
    data: GenerateReportWithoutLookThroughSchemaType,
  ) => {
    generatePdfMutation.mutate(data);
  };

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          radius="full"
          style={{
            border: "solid 2px transparent",
            backgroundImage: `linear-gradient(hsl(var(--heroui-background)), hsl(var(--heroui-background))), linear-gradient(83.87deg, #F54180, #9353D3)`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
          endContent={<ChevronDownIcon className="h-4 w-4" />}
        >
          {t("reports.generate_without_lookthrough_action")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="space-y-4 p-1 w-full"
        >
          <div>
            <label className="text-xs text-primary">
              {t(
                `g4_download_center.button.reportWithoutLookThrough.portfolio`,
                { ns: "tables" },
              )}
            </label>
            <CheckboxGroup
              value={selectedPortflios}
              onValueChange={handlePortfolioSelectionChange}
              className="max-h-40 overflow-y-auto border rounded-lg p-2 overflow-x-hidden"
            >
              {["GARAG", "GARET"].map((page) => (
                <Checkbox
                  key={page}
                  value={page!}
                  className="text-xs"
                  size="sm"
                >
                  {page}
                </Checkbox>
              ))}
            </CheckboxGroup>
            <Divider className="my-4" />
            <DateField
              name={generateWithoutLookThroughFields.reportingDate}
              label={t(
                `g4_download_center.button.reportWithoutLookThrough.reportingDate`,
                { ns: "tables" },
              )}
              watch={watch}
              setValue={setValue}
              register={register}
              required={true}
            />
          </div>

          <footer className="mt-4 w-full">
            <Button
              color="primary"
              radius="full"
              size="sm"
              type="submit"
              className="w-24"
              isDisabled={!canSubmit}
              isLoading={generatePdfMutation.isPending}
            >
              Générer
            </Button>
          </footer>
        </form>
      </PopoverContent>
    </Popover>
  );
}
